import { useState, useEffect, createRef, useRef } from 'react'
import { useToast } from '../../../contexts/ToastContext'
import TabSectionHeader from '../Jobs/TabSectionHeader'
import QuoteEmailDialog from './QuoteEmailDialog'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import QuoteDocumentRow from '../../QuoteDocumentRow'
import { DateFormatter } from '../../../data/helper'
import { Button, SplitButton } from "@progress/kendo-react-buttons";
import {
  FormTextArea,
  FormDropDownList,
  FormNumericTextBox,
  FormDatePicker,
  FormDropDownListFilter,
  FormCheckbox,
  FormDateTimePicker,
  FormSwitch,
} from '../FormComponents'
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from '@progress/kendo-react-layout'
import {
  Upload,
  ExternalDropZone,
  UploadOnStatusChangeEvent,
  UploadFileInfo,
  UploadFileStatus,
  UploadListItemProps,
} from '@progress/kendo-react-upload'
import { MultiSelect, DropDownList } from '@progress/kendo-react-dropdowns'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { Label } from '@progress/kendo-react-labels'
import { TextArea } from '@progress/kendo-react-inputs'
import axios from 'axios';
import { getTargetQuote, getQuoteDocuments, QuoteTemplate, QuoteTemplateFile, getQuoteTemplateFiles, incrementFollowUpCount, quoteLost } from '../../../data/quoteData'
import { getQuoteLostReasons } from '../../../data/quoteLostReasonData'
import { getSitesDropdown } from '../../../data/siteData'
import { getClientsDropdown } from '../../../data/clientData'
import { getWorktypeDropdown } from '../../../data/worktypeData'
import { getNotifiableWorks } from '../../../data/notifiableWorkData'
import { getEnquiryDropdown } from '../../../data/enquiryData'
import { getEmployeeDropdown } from '../../../data/employeeData'
import { getDepartmentDropdown } from '../../../data/departmentData'
import { getEnquirysourceDropdown } from '../../../data/enquirysourceData'
import { getLikelihoodDropdown } from '../../../data/likelihoodData'
import { requiredValidator } from '../Validators'

import { fileDocumentTypes, dropDown } from '../../../data/interfaces'
import { FaFilePdf, FaFileCsv, FaFileAlt, FaFileImage, FaTimesCircle, FaFlag, FaFileSignature, FaPaperclip, FaRegStickyNote, FaLink, FaHammer, FaEnvelope } from 'react-icons/fa'

import SkeletonForm from '../../Skeletons/SkeletonForm'
import Divider from '../../Divider/Divider'
import { addNoteDTO, addNote } from '../../../data/notes';
import Notes from '../../../pages/Notes/Notes'
import EntityLinks from '../Links/EntityLinks';
import { getJobsForDropdownClientSpecific } from '../../../data/jobData'
import { useNavigate } from 'react-router-dom'
import { getAddressesForClientDropdown, getClientsForClientsDropdown, getClientByIdV2, getContactById } from '../../../data/accountsData'
import WorkingSectionsView, { WorkingSectionsViewHandle } from '../../grids/WorkingSectionsView'
import WorkingsAdjustmentSummary from '../../grids/WorkingsAdjustmentSummary'
import { MaterialWorking, LabourWorking } from '../../../data/interfaces'
import { 
  getMaterialWorkingsByQuote, 
  getLabourWorkingsByQuote
} from '../../../data/workingsData'

const apiURL = process.env.REACT_APP_API_URL

const uploadRef = createRef<Upload>()

const icon = (fileName: string) => {
  if (fileName.includes('.pdf')) {
    return (
      <FaFilePdf
        style={{
          fontSize: '20pt',
          color: 'rgb(33, 67, 59)',
          marginRight: '1rem',
        }}
      />
    )
  } else if (fileName.includes('.csv')) {
    return (
      <FaFileCsv
        style={{
          fontSize: '20pt',
          color: 'rgb(33, 67, 59)',
          marginRight: '1rem',
        }}
      />
    )
  } else if (fileName.includes('.txt')) {
    return (
      <FaFileAlt
        style={{
          fontSize: '20pt',
          color: 'rgb(33, 67, 59)',
          marginRight: '1rem',
        }}
      />
    )
  } else if (fileName.includes('.jpg')) {
    return (
      <FaFileImage
        style={{
          fontSize: '20pt',
          color: 'rgb(33, 67, 59)',
          marginRight: '1rem',
        }}
      />
    )
  } else if (fileName.includes('.png')) {
    return (
      <FaFileImage
        style={{
          fontSize: '20pt',
          color: 'rgb(33, 67, 59)',
          marginRight: '1rem',
        }}
      />
    )
  } else {
    return (
      <FaFileAlt
        style={{
          fontSize: '20pt',
          color: 'rgb(33, 67, 59)',
          marginRight: '1rem',
        }}
      />
    )
  }
}

const CustomListItemUI = (props: UploadListItemProps) => {
  return (
    <ul>
      {props.files.map((file) => (
        <div
          style={{ fontSize: '10pt', fontFamily: 'Heebo, Sans-Serif' }}
          key={file.name}
        >
          {icon(file.name)}
          {file.name}
        </div>
      ))}
    </ul>
  )
}

const EditQuoteForm = (props: any) => {
  const token = localStorage.getItem('authToken')
  const dateFormatter = new DateFormatter()

  const [isDeleted, setIsDeleted] = useState(false)
  const [loading, setLoading] = useState(true)
  // Reason prompt shown when marking the quote as lost
  const [lostReasonDialogVisible, setLostReasonDialogVisible] = useState(false)
  const [lostReasons, setLostReasons] = useState<dropDown[]>([])
  const [lostReasonsLoading, setLostReasonsLoading] = useState(false)
  const [selectedLostReason, setSelectedLostReason] = useState<dropDown | null>(null)
  const quoteId = props.onQuoteId
  const [jobId, setJobId] = useState(0)
  const [siteData, setSiteData] = useState<dropDown[]>([])
  const [siteDataOrig, setSiteDataOrig] = useState<dropDown[]>([])
  const [clientData, setClientData] = useState<dropDown[]>([])
  const [clientDataOrig, setClientDataOrig] = useState<dropDown[]>([])
  const [enquirysourceData, setEnquirysourceData] = useState<dropDown[]>([])
  const [likelihoodData, setLikelihoodData] = useState<dropDown[]>([])
  const [worktypeData, setWorktypeData] = useState<dropDown[]>([])
  const [notifiableWorksOptions, setNotifiableWorksOptions] = useState<string[]>([])
  const [notifiableWorks, setNotifiableWorks] = useState<string[]>([])
  // Quote PDFs to copy onto the new job with their prices blacked out, for the app.
  const [priceFreeIds, setPriceFreeIds] = useState<number[]>([])
  // The new job's description, offered as the quote's description and "As per
  // <quote ref>", and editable.
  const [jobDescription, setJobDescription] = useState('')
  const [showEmail, setShowEmail] = useState(false)
  const [employeeData, setEmployeeData] = useState<dropDown[]>([])
  const [employeeDataOrig, setEmployeeDataOrig] = useState<dropDown[]>([])
  const [employeeProjectData, setEmployeeProjectData] = useState<dropDown[]>([])
  const [convertText, setConvertText] = useState('New')
  const [jobDropdownData, setJobDropdownData] = useState<dropDown[]>([])

  const { showToast } = useToast()
  const [displayConvert, setDisplayConvert] = useState(false)
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const workingSectionsRef = useRef<WorkingSectionsViewHandle>(null)
  const [files, setFiles] = useState<UploadFileInfo[]>([])

  const [folderStructureToggleSet, setFolderStructureToggleSet] = useState(false);
  const [projectUrlAlreadySet, setProjectUrlAlreadySet] = useState(false);
  
  // Workings state
  const [materialWorkings, setMaterialWorkings] = useState<MaterialWorking[]>([])
  const [labourWorkings, setLabourWorkings] = useState<LabourWorking[]>([])
  const [workingsAdjustmentPercent, setWorkingsAdjustmentPercent] = useState<number | null>(null)
  /** list of files */
  const [quoteFiles, setQuoteFiles] = useState<QuoteTemplateFile[]>([]);

  const [quoteUrlAlreadySet, setQuoteUrlAlreadySet] = useState(false);
  const [resetQuoteTemplate, setResetQuoteTemplate] = useState(false);
  const [isBadDebter, setIsBadDebter] = useState(false);

  /**
* Retrieves the quote template files and sets them in the state.
* @returns {Promise<void>} A promise that resolves when the quote template files are retrieved and set in the state.
*/
  const getQuoteFiles = async () => {
    const files = await getQuoteTemplateFiles();
    setQuoteFiles(files);

  }

  useEffect(() => {
    getQuoteFiles();
  }, []);

  const quoteConvertItems = [
    {
      text: 'Convert to Existing Job',
      value: 'existing'
    },
    {
      text: 'Convert to New Job',
      value: 'new'
    }
  ];

  const [documentData, setDocumentData] = useState<fileDocumentTypes[]>([
    {
      documentId: 0,
      fileName: '',
      fileExtension: '',
      appCanView: false,
    },
  ])

  const handleSelectedTab = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected)
  }

  const [formState, setFormState] = useState({
    contact: {
      contactId: 0,
      title: "",
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
    },
    site: {
      siteId: 0,
      siteAddr: '',
      postcode: '',
    },
    quoteUrl: '',
    jobId: 0,
    jobRef: 0,
    enquiryRef: 0,
    refFormatted: '',
    siteAddr: {
      text: '',
      value: 0,
    },
    clientName: {
      text: '',
      value: 0,
    },
    enquirysourceId: {
      text: '',
      value: 0,
    },
    likelihoodId: {
      text: '',
      value: 0,
    },
    departmentId: {
      text: '',
      value: 0,
    },
    description: '',
    worktypeId: {
      text: '',
      value: 0,
    },
    cost: 0,
    followupDate: '',
    wonDate: '',
    lostDate: '',
    lostReason: '',
    enquiryId: 0,
    employeeAssignedToId: {
      text: '',
      value: 0,
    },
    employeeProjectManagerId: 1,
    startDate: '',
    endDate: '',
    quoteId: quoteId,
    firstHourInvoiced: false,
    quoteTemplateId: 0,
    shortDescription: "",
    worksDescription: "",
    qualsDescription: "",
    value: 0,
    isSent: false,
    sentDate: null as string | null,
    projectFolderUrl: null,
    enquiryFormattedId: "",
    requireFollowups: true
  })
  //console.log(formState);
  const animationStyle = displayConvert
    ? { animation: 'slide-in 0.5s ease-in-out' }
    : { animation: 'slide-out 0.5s ease-in-out' }

  const quotePdfs = documentData.filter(
    (d) => d.documentId > 0 && d.fileName.toLowerCase().endsWith('.pdf')
  )

  // Each time the convert box opens, tick the newest PDF - most likely the quote itself.
  useEffect(() => {
    if (!displayConvert) return
    const newest = documentData
      .filter((d) => d.documentId > 0 && d.fileName.toLowerCase().endsWith('.pdf'))
      .reduce<fileDocumentTypes | null>((a, d) => (!a || d.documentId > a.documentId ? d : a), null)
    setPriceFreeIds(newest ? [newest.documentId] : [])
  }, [displayConvert, documentData])

  // The quote's own description first, then "As per <quote ref>" in a
  // paragraph of its own.
  useEffect(() => {
    if (!displayConvert) return
    const quoteDescription = (formState.description ?? '').trim()
    const asPer = `As per ${formState.refFormatted}`
    setJobDescription(quoteDescription ? `${quoteDescription}\n\n${asPer}` : asPer)
  }, [displayConvert, formState.description, formState.refFormatted])

  const togglePriceFree = (documentId: number) =>
    setPriceFreeIds((prev) =>
      prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId]
    )

  const handleConvertDisplay = (e: any) => {
    if (e.nativeEvent && typeof e.nativeEvent.preventDefault === 'function') {
      e.nativeEvent.preventDefault();
    }
    setDisplayConvert(!displayConvert)
  }

  useEffect(() => {
    setWorktypeData(props.updatedWorkTypes)
  }, [props.updatedWorkTypes])

  useEffect(() => {
    const fetchSiteData = async () => {
      const siteData = await getSitesDropdown('')
      setSiteData(siteData)
      setSiteDataOrig(siteData)
    }

    const fetchClientData = async () => {
      const clientData = await getClientsDropdown('')
      setClientData(clientData)
      setClientDataOrig(clientData)
    }

    const fetchEnquirysourceData = async () => {
      const enquirysourceData = await getEnquirysourceDropdown()
      setEnquirysourceData(enquirysourceData)
    }

    const fetchLikelihoodData = async () => {
      const likelihoodData = await getLikelihoodDropdown()
      setLikelihoodData(likelihoodData)
    }

    const fetchWorktypeData = async () => {
      const worktypeData = await getWorktypeDropdown()
      setWorktypeData(worktypeData)
    }

    const fetchNotifiableWorks = async () => {
      const works = await getNotifiableWorks()
      setNotifiableWorksOptions(works)
    }

    const fetchEmployeeData = async () => {
      const employeeData = await getEmployeeDropdown('')
      setEmployeeData(employeeData)
      setEmployeeDataOrig(employeeData)
    }

    const fetchDocumentData = async () => {
      const documentData = await getQuoteDocuments(quoteId)
      setDocumentData(documentData)
    }

    const fetchWorkingsData = async () => {
      try {
        const materialData = await getMaterialWorkingsByQuote(quoteId)
        const labourData = await getLabourWorkingsByQuote(quoteId)
        
        setMaterialWorkings(materialData)
        setLabourWorkings(labourData)
        
      } catch (error) {
        console.error('Error fetching workings data:', error)
      }
    }

    const fetchPlaceholderData = async () => {
      let quote = await getTargetQuote(quoteId)
      setWorkingsAdjustmentPercent(quote.workingsAdjustmentPercent ?? null)
      console.log("res", quote)
      props.onQuoteLoaded?.(quote.refFormatted ?? '')
      console.log("requireFollowups from API:", quote.requireFollowups)
      const requireFollowupsValue = quote.requireFollowups === undefined || quote.requireFollowups === null ? true : quote.requireFollowups
      console.log("requireFollowups after default:", requireFollowupsValue)
      setFormState({
        contact: {
          contactId: quote.contact?.contactId as number,
          title: quote.contact?.title ?? '',
          firstname: quote.contact?.firstname ?? '',
          lastname: quote.contact?.lastname ?? '',
          phone: quote.contact?.phone ?? '',
          email: quote.contact?.email ?? '',
        },
        site: {
          siteId: quote.site.siteId ?? 0,
          siteAddr: quote.site.siteAddr ?? '',
          postcode: quote.site.postcode ?? '',
        },
        jobId: quote.jobId,
        quoteUrl: quote.quoteUrl,
        jobRef: quote.jobRef,
        enquiryRef: quote.enquiryRef,
        enquiryFormattedId: quote.enquiry?.refFormatted,
        refFormatted: quote.refFormatted,
        isSent: quote.isSent,
        sentDate: quote.sentDate ?? null,
        siteAddr: {
          text: quote.site.siteAddr + ", " + quote.site.postcode,
          value: quote.site.siteId || 0,
        },
        clientName: {
          text: quote.client.clientName + " - " + quote.client.accountRef,
          value: quote.client.clientId || 0,
        },
        enquirysourceId: {
          text: quote.enquirysource.description,
          value: quote.enquirysource.enquirysourceId || 0,
        },
        likelihoodId: {
          text: quote.likelihood?.description || '',
          value: quote.likelihood?.likelihoodId || 0,
        },
        departmentId: {
          text: '',
          value: 0,
        },
        description: quote.description,
        worktypeId: {
          text: quote.worktype.description,
          value: quote.worktype.worktypeId || 0,
        },
        cost: quote.cost,
        followupDate: quote.followupDate,
        wonDate: quote.wonDate,
        lostDate: quote.lostDate,
        lostReason: quote.lostReason ?? '',
        enquiryId: quote.enquiryId || 0,
        employeeAssignedToId: {
          text: quote.employeeAssignedTo.employeeName,
          value: quote.employeeAssignedTo.employeeId || 0,
        },
        employeeProjectManagerId: quote.employeeAssignedTo.employeeId || 0,
        startDate: '',
        endDate: '',
        quoteId: quoteId,
        firstHourInvoiced: false,
        quoteTemplateId: quote.quoteTemplateId || 0,
        shortDescription: quote.shortDescription || "",
        worksDescription: quote.worksDescription || "",
        qualsDescription: quote.qualsDescription || "",
        value: quote.value || 0,
        projectFolderUrl: quote.projectFolderUrl,
        requireFollowups: requireFollowupsValue
      })

      setProjectUrlAlreadySet(quote.projectFolderUrl !== null ? true : false)
      setQuoteUrlAlreadySet(quote.quoteUrl !== null ? true : false)

      // Check if client is a bad debter
      if (quote.client?.clientId) {
        const clientData = await getClientByIdV2(quote.client.clientId);
        setIsBadDebter(clientData.badDebter || false);
      }

      setLoading(false)

      if (quote.lostDate) {
        setIsDeleted(true)
      } else {
        setIsDeleted(false)
      }
    }

    fetchEmployeeData()
    fetchSiteData()
    fetchClientData()
    fetchWorktypeData()
    fetchNotifiableWorks()
    fetchDocumentData()
    fetchEnquirysourceData()
    fetchWorkingsData()
    
    // Fetch likelihood data first, then placeholder data
    const loadData = async () => {
      await fetchLikelihoodData()
      await fetchPlaceholderData()
    }
    loadData()
  }, [quoteId])

  useEffect(() => {
    const fetchJobData = async (id: number) => {
      await getJobsForDropdownClientSpecific(id).then((results) => {
        setJobDropdownData(results)
      })
    }

    fetchJobData(formState.clientName.value);
  }, [formState.clientName.value])

  //Handle Submits
  const handleSubmit = () => {
    // The adjustment percentage lives outside formState because it belongs to
    // the workings tab rather than the quote form, so fold it in here.
    props.onEditSubmit(
      { ...formState, workingsAdjustmentPercent },
      quoteId
    )
  }

  const handleConvertSubmit = (event: any) => {
    event.preventDefault()
    if (notifiableWorks.length === 0) {
      showToast('Please select at least one notifiable work', 'error')
      return
    }
    if (jobDescription.trim() === '') {
      showToast('Please enter a description for the job', 'error')
      return
    }
    // console.log('dataItem: ', formState)
    // alert(JSON.stringify(formState, null, 2));
    props.onConvertSubmit({
      ...formState,
      notifiableWorks,
      priceFreeDocumentIds: priceFreeIds,
      jobDescription: jobDescription.trim(),
    })
  }

  const handleConvertSubmitExisting = (event: any) => {
    event.preventDefault()
    // notifiableWorks are appended to the existing job's works during the update.
    props.onConvertToExistingSubmit({ ...formState, notifiableWorks }, jobId, quoteId)
  }

  // Losing a quote has to record why, so the button opens the reason prompt
  // rather than marking it lost straight away.
  const handleQuoteLost = async () => {
    setSelectedLostReason(null)
    setLostReasonDialogVisible(true)

    try {
      setLostReasonsLoading(true)
      setLostReasons(await getQuoteLostReasons())
    } catch (err) {
      console.error(err)
      showToast('Failed to load lost reasons', 'error')
    } finally {
      setLostReasonsLoading(false)
    }
  }

  const confirmQuoteLost = async () => {
    if (!selectedLostReason) {
      return
    }

    try {
      await quoteLost(quoteId, selectedLostReason.value)
      showToast('Quote marked as lost', 'success')
      setLostReasonDialogVisible(false)
      setIsDeleted(true)

      // Close panel and refresh grid
      if (props.onClose) {
        props.onClose()
      }
      if (props.onRefresh) {
        props.onRefresh()
      }
    } catch (err) {
      console.error(err)
      showToast('Failed to mark quote as lost', 'error')
    }
  }

  const handleQuoteWon = async () => {
    try {
      const token = localStorage.getItem('authToken')
      await fetch(
        `https://${apiURL}/quotes/reinstate/${quoteId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )
      showToast('Quote marked as won', 'success')
      setIsDeleted(false)

      const fresh = await getTargetQuote(quoteId)
      setFormState(s => ({
        ...s,
        wonDate: fresh.wonDate,
        lostDate: fresh.lostDate,
        lostReason: fresh.lostReason ?? ''
      }))
      
      // Close panel and refresh grid
      if (props.onClose) {
        props.onClose()
      }
      if (props.onRefresh) {
        props.onRefresh()
      }
    } catch (err) {
      console.error(err)
      showToast('Failed to mark quote as won', 'error')
    }
  }


  const handleUploadSuccess = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState)

    // onStatusChange also fires when an upload starts or fails, so only act on
    // files that actually finished uploading. Refresh the document list so the
    // newly uploaded file appears without needing to reload the form.
    const uploaded = event.affectedFiles.some(
      (file) => file.status === UploadFileStatus.Uploaded
    )
    const failed = event.affectedFiles.some(
      (file) => file.status === UploadFileStatus.UploadFailed
    )

    if (uploaded) {
      showToast('Succesfully uploaded new document', 'success')
      getQuoteDocuments(quoteId).then(setDocumentData)
    } else if (failed) {
      showToast('Failed to upload document', 'error')
    }
  }

  const handleDocumentDelete = (documentId: number) => {
    props.onDocumentDelete(documentId)
    setDocumentData(
      documentData.filter((document: any) => document.documentId !== documentId)
    )
  }

  useEffect(() => {
    const fetchDocumentData = async () => {
      const documentData = await getQuoteDocuments(quoteId)
      setDocumentData(documentData)
    }

    fetchDocumentData()
  }, [files])

  //Search filters
  const searchSiteDropdown = (searchTerm: string) => {
    const fetchSiteData = async (search: string) => {
      await getSitesDropdown(search).then((result) => {
        setSiteData(result)
      })
    }

    fetchSiteData(searchTerm)
  }

  const searchClientDropdown = (searchTerm: string) => {
    const fetchClientData = async (search: string) => {
      await getClientsDropdown(search).then((result) => {
        setClientData(result)
      })
    }

    fetchClientData(searchTerm)
  }

  const searchEmployeeDropdown = (searchTerm: string) => {
    const fetchEmployeeData = async (search: string) => {
      await getEmployeeDropdown(search).then((result) => {
        setEmployeeData(result)
      })
    }

    fetchEmployeeData(searchTerm)
  }

  const searchEmployeeProjectDropdown = (searchTerm: string) => {
    const fetchEmployeeData = async (search: string) => {
      await getEmployeeDropdown(search).then((result) => {
        setEmployeeProjectData(result)
      })
    }

    fetchEmployeeData(searchTerm)
  }

  const handleAddSite = () => {
    props.onAddSiteToggle()
  }
  const handleAddWorkType = () => {
    props.onAddWorkTypeToggle()
  }

  const handleConvertChange = (e: any) => {
    if (e.item.value === 'new') {
      setConvertText('New')
    } else {
      setConvertText('Existing')
    }
  }

  const addNoteDTO: addNoteDTO = {
    enquiryId: null,
    quoteId: quoteId,
    jobId: null
  };

  const handleFollowedUp = async () => {
    try {
      // Increment the follow-up count
      await incrementFollowUpCount(quoteId);
      
      // Add a note about the follow-up
      const currentUser = localStorage.getItem('userName') || 'User';
      const followUpNote = `Follow-up actioned by ${currentUser} on ${new Date().toLocaleDateString()}`;
      await addNote(addNoteDTO, followUpNote, [] as any);
      
      showToast('Follow-up recorded successfully', 'success');
      
      // Refresh the quote data
      if (props.onRefresh) {
        props.onRefresh();
      }
    } catch (error: any) {
      console.error('Error recording follow-up:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to record follow-up';
      showToast(errorMessage, 'error');
    }
  };

  const generatePDF = () => {
    // const url = "https://prod-43.westeurope.logic.azure.com/workflows/aaf5feddbbc24bca8a836c80f1eb3497/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=R2Aq1DIZfuMP5opIE8uAbUddgwXYKUI3jTqv7h7V708"
    // const body = {
    //   QuoteId: quoteId
    // }
    // axios.post(url, body)
    // .then(res =>  console.log(res))
    // .catch(err => console.log(err))
  }

  const checkForProjectUrl = () => {
    const url = formState.projectFolderUrl;
    if (url !== null) {
      const path: string = url;
      if (path.includes("https://")) {
        return <a href={path} target="_blank" rel="noreferrer">View folder structure</a>
      } else {
        return <div><strong>Generating folder structure... Please check back soon.</strong></div>
      }
    }
  }

  const checkForQuoteUrl = () => {
    const url = formState.quoteUrl;
    if (url !== null) {
      const path: string = url;
      if (path.includes("https://")) {
        const isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || /Mac/i.test(navigator.userAgent);
        const href = isMac ? `ms-word:ofe|u|${path}` : path;
        return { hasUrl: true, content: <a href={href} target="_blank" rel="noreferrer">View Quote</a>}
      } else {
        return {hasUrl: false, content: <div><strong>Generating Quote... Please check back soon.</strong></div>}
      }
    }
  }

  const navigate = useNavigate();

  const [accountData, setAccountData] = useState<dropDown[]>([]);


  const getAndStoreAddressesForCustomer = async (customerId: number) => {
    try { 
      const res = await getAddressesForClientDropdown(customerId);
      setSiteData(res);
      setFormState((prev) => ({
        ...prev,
        siteAddr: {
          text: res.length === 1 ? res[0]?.text || '' : formState.siteAddr.text,
          value: res.length === 1 ? res[0]?.value || 0 : formState.siteAddr.value,
        }
      }))
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if(formState.clientName.value !== 0) {
      getAndStoreAddressesForCustomer(formState.clientName.value);
    }
  }, [formState.clientName])

  /**
   * End Account and address selection
   */  

  // Contacts for client
  const [contactsForClient, setContactsForClient] = useState<dropDown[]>([]);

  // The contact dropdown only carries a name and id, so pull the full record to
  // keep the details header in step with the selection.
  const refreshContactDetails = async (contactId: number) => {
    if (!contactId) return
    try {
      const contact = await getContactById(contactId)
      setFormState((prev: any) => ({
        ...prev,
        contact: {
          ...prev.contact,
          contactId,
          title: contact.title ?? '',
          firstname: contact.firstname ?? '',
          lastname: contact.lastname ?? '',
          phone: contact.phone ?? '',
          email: contact.email ?? '',
        }
      }))
    } catch (err) {
      console.log(err)
    }
  }

  const getAndStoreContactsForCustomer = async (customerId: number) => {
    try {
      const res = await getClientsForClientsDropdown(customerId);
      setContactsForClient(res);
      setFormState((prev: any) => ({
        ...prev,
        // Spread the existing contact so the loaded name/phone/email survive -
        // this fires on load as soon as the client is set, and replacing the
        // object wholesale would blank the details header.
        contact: {
          ...prev.contact,
          contactId: res.length === 1 ? res[0]?.value || 0 : prev.contact.contactId,
        }
      }))

    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (formState.clientName.value !== 0) {
      getAndStoreContactsForCustomer(formState.clientName.value);
    }
  }, [formState.clientName]) 
  
  console.log(formState)

  // Single source of truth for the tab order. Kendo TabStrip assigns each
  // rendered <TabStripTab> a numeric index in DOM order, and it filters out
  // falsy children — so when lockEdit hides File Upload every later index
  // shifts down by one. This array mirrors that exactly, and the rail below
  // maps a section id -> index via indexOf, so the two can never drift apart.
  const quoteTabOrder = [
    'quoteForm',
    ...(props.lockEdit ? [] : ['files']),
    'workings',
    'notes',
    'links',
  ]

  const quoteNavGroups = [
    {
      label: 'Details',
      items: [
        { id: 'quoteForm', label: 'Quote Form' },
        { id: 'workings', label: 'Workings' },
      ],
    },
    {
      label: 'Attachments',
      items: [
        ...(props.lockEdit ? [] : [{ id: 'files', label: 'Files' }]),
        { id: 'notes', label: 'Notes' },
        { id: 'links', label: 'Links' },
      ],
    },
  ]

  return loading ? (
    <SkeletonForm />
  ) : (
    <div className='form__wrapper'>
      <div style={{ display: 'flex', width: '100%', flex: 1, minHeight: 0, height: '100%' }}>
        {
          props.isQuoteForm ? (
            <>
              <div style={{ padding: "20px", width: "100%" }}>
                <FormDropDownList
                  disabled={props.lockEdit}
                  data={props.quoteTemplates.map((file: QuoteTemplate) => ({ text: file.name, value: file.quoteTemplateId }))}
                  label={"Select Template"}
                  defaultValue={formState.quoteTemplateId}
                  defaultText={props.quoteTemplates.find((file: QuoteTemplate) => file.quoteTemplateId === formState.quoteTemplateId)?.name || ""}
                  onChange={(e: any) => {
                    const id = e.value.value;
                    const template = props.quoteTemplates.find((file: QuoteTemplate) => file.quoteTemplateId === id);
                    setFormState((prev) => ({
                      ...prev,
                      shortDescription: template?.shortDescription || "",
                      worksDescription: template?.worksDescription || "",
                      qualsDescription: template?.qualificationsDescription || "",
                      quoteTemplateId: template?.quoteTemplateId || 0,
                      value: template?.totalCost || 0,
                    }))
                  }}
                />

                <FormTextArea
                  disabled={props.lockEdit}
                  defaultValue={formState.shortDescription}
                  name={"shortDescription"}
                  label={"Short Description"}
                  onChange={(e: any) => {
                    setFormState({
                      ...formState,
                      shortDescription: e.value
                    })
                  }}
                />

                <FormTextArea
                  disabled={props.lockEdit}
                  defaultValue={formState.worksDescription}
                  name={"worksDescription"}
                  label={"Works Description"}
                  onChange={(e: any) => {
                    setFormState({
                      ...formState,
                      worksDescription: e.value
                    })
                  }}
                />

                <FormTextArea
                  disabled={props.lockEdit}
                  defaultValue={formState.qualsDescription}
                  name={"qualsDescription"}
                  label={"Qualifcations Description"}
                  onChange={(e: any) => {
                    setFormState({
                      ...formState,
                      qualsDescription: e.value
                    })
                  }}
                />

                <FormNumericTextBox
                  disabled={props.lockEdit}
                  value={formState.value}
                  name={"value"}
                  label={"Total Cost"}
                  onChange={(e: any) => {
                    setFormState({
                      ...formState,
                      shortDescription: e.value
                    })
                  }}
                />
                {
                  props.lockEdit === false ? (
                    <div className='button__container'>
                      <button
                        onClick={() => {
                          handleSubmit()
                        }}
                        className='form__edit__button edit'
                      >
                        Save changes
                      </button>
                      <button
                        onClick={() => generatePDF()}
                        className='form__edit__button accept'
                      >
                        Create PDF
                      </button>
                      <button
                        onClick={() => false}
                        className='form__edit__button accept'
                      >
                        Download PDF
                      </button>
                    </div>
                  ) : null
                }
              </div>
            </>
          ) : (
            <>
            <nav className='jobnav__rail'>
              {quoteNavGroups.map((group) => (
                <div key={group.label} className='jobnav__group'>
                  <div className='jobnav__group-label'>{group.label}</div>
                  {group.items.map((item) => {
                    const idx = quoteTabOrder.indexOf(item.id)
                    const active = selectedTab === idx
                    return (
                      <button
                        key={item.id}
                        type='button'
                        className={`jobnav__item${active ? ' active' : ''}`}
                        onClick={() => setSelectedTab(idx)}
                      >
                        <span className='jobnav__item-label'>{item.label}</span>
                        {typeof (item as any).count === 'number' && (item as any).count > 0 && (
                          <span className='jobnav__badge'>{(item as any).count}</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              ))}
            </nav>
            <TabStrip
              selected={selectedTab}
              onSelect={handleSelectedTab}
              className='form__tabstrip__container jobnav__tabstrip'
              animation={false}
            >
              <TabStripTab title='Quote Form'>
                <div style={{ paddingTop: '1rem' }}>
                  <TabSectionHeader
                    icon={<FaFileSignature />}
                    title="Quote Details"
                    description="Core details and settings for this quote"
                  />
                </div>
                <Form
                  initialValues={{
                    description: formState.description,
                  }}
                  onSubmit={handleSubmit}
                  render={(formRenderProps) => (
                    <FormElement
                      style={{
                        padding: '1rem',
                        width: '100%',
                        marginBottom: '5rem',
                      }}
                    >
                      <fieldset className={'k-form-fieldset'}>
                        {(formState.enquiryFormattedId || formState.jobRef) && (
                          <div className='jobmeta__chips'>
                            {formState.enquiryFormattedId ? (
                              <span className='jobmeta__chip' onClick={() => window.open(`/enquiries/?id=${formState.enquiryId}`, "_blank")}>
                                Enquiry {formState.enquiryFormattedId}
                              </span>
                            ) : null}
                            {formState.jobRef ? (
                              <span className='jobmeta__chip' onClick={() => window.open(`/jobs/?id=${formState.jobId}`, "_blank")}>
                                Job J{formState.jobRef}
                              </span>
                            ) : null}
                          </div>
                        )}

                        <div className='form__details__subcontainer'>
                          <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Contact: {`${formState.contact?.title || ""} ${formState.contact?.firstname || ""} ${formState.contact?.lastname || ""}`}</p>
                          <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Address: {`${formState.site.siteAddr || ""}`}</p>
                          <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Phone: {formState.contact.phone || ""}</p>
                          <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Email: <a href={`mailto:${formState.contact.email}`}>{formState.contact.email}</a></p>
                        </div>

                        <Divider>
                          General Info
                        </Divider>

                        <Field
                      disabled={props.lockEdit}
                      data={clientData}
                      originalData={clientDataOrig}
                      name={'clientName'}
                      component={FormDropDownListFilter}
                      label={'Choose a client:'}
                      setData={setClientData}
                      searchDropdown={searchClientDropdown}
                      defaultItem={formState.clientName}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          clientName: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : true}
                      buttonText={'Add Account'}
                      buttonAction={() => {
                        props.setAddAccountVisible(true)
                        props.setAccountMode("add");
                        props.setAccountInitialTab(0);
                      }}                  
                    />

<Field
                        disabled={props.lockEdit}
                        data={contactsForClient}
                        originalData={siteDataOrig}
                        name={'contact'}
                        required={true}
                        component={FormDropDownListFilter}
                        label={'Contact:'}
                        setData={setContactsForClient}
                        onOpen={() => getAndStoreContactsForCustomer(formState.clientName.value)}
                        searchDropdown={searchSiteDropdown}
                        defaultItem={contactsForClient.find(item => Number(item.value) === Number(formState.contact.contactId))}
                        onChange={(e) => {
                          const contactId = parseInt(e.value.value, 10);
                          refreshContactDetails(contactId);
                          setFormState({
                            ...formState,
                            contact: {
                              ...e.value,
                              contactId,
                            }
                          })
                        }}
                        hasButton={props.lockEdit ? false : formState.clientName.value > 0}
                        buttonText={'Add Contact'}
                        buttonAction={() => {
                          props.setAccountMode("edit");
                          props.setSelectedAccountId(formState.clientName.value);
                          props.setAccountInitialTab(2);
                          props.setAddAccountVisible(true)
                        }}
                      />                      

                    <Field
                      disabled={props.lockEdit}
                      data={siteData}
                      originalData={siteDataOrig}
                      name={'address'}
                      component={FormDropDownList}
                      label={'Address:'}
                      setData={setSiteData}
                      onOpen={() => getAndStoreAddressesForCustomer(formState.clientName.value)}
                      searchDropdown={searchSiteDropdown}
                      defaultItem={siteData.find(item => Number(item.value) === Number(formState.siteAddr.value))}
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          siteAddr: {
                            ...e.value,
                            value: parseInt(e.value.value, 10),
                          },
                        })
                      }}
                      hasButton={props.lockEdit ? false : formState.clientName.value > 0}
                      buttonText={'Add Address'}
                      buttonAction={() => {
                        props.setAccountMode("edit");
                        props.setSelectedAccountId(formState.clientName.value);
                        props.setAccountInitialTab(1);
                        props.setAddAccountVisible(true)
                      }}
                    />                        

                        {/* <Field
                          disabled={props.lockEdit}
                          data={clientData}
                          originalData={clientDataOrig}
                          name={'clientName'}
                          component={FormDropDownListFilter}
                          label={'Choose the client:'}
                          setData={setClientData}
                          searchDropdown={searchClientDropdown}
                          defaultItem={formState.clientName}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              clientName: {
                                ...e.value,
                                value: parseInt(e.value.value, 10),
                              },
                            })
                          }}
                        />

                        <Field
                          disabled={props.lockEdit}
                          data={siteData}
                          originalData={siteDataOrig}
                          name={'siteAddr'}
                          component={FormDropDownListFilter}
                          searchDropdown={searchSiteDropdown}
                          label={'Choose the site:'}
                          defaultItem={formState.siteAddr}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              siteAddr: {
                                ...e.value,
                                value: parseInt(e.value.value, 10),
                              },
                            })
                          }}
                          hasButton={props.lockEdit ? false : true}
                          buttonText={'Add Site'}
                          buttonAction={handleAddSite}
                        /> */}

                        <Field
                          disabled={props.lockEdit}
                          data={enquirysourceData}
                          name={'enquirysourceId'}
                          component={FormDropDownList}
                          label={'Choose enquiry source:'}
                          setData={setEnquirysourceData}
                          defaultText={formState.enquirysourceId.text}
                          defaultValue={formState.enquirysourceId.value}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              enquirysourceId: {
                                ...e.value,
                                value: parseInt(e.value.value, 10),
                              },
                            })
                          }}
                        />

                        <Field
                          disabled={props.lockEdit}
                          data={likelihoodData}
                          name={'likelihoodId'}
                          component={FormDropDownList}
                          label={'Choose likelihood:'}
                          setData={setLikelihoodData}
                          defaultText={formState.likelihoodId.text}
                          defaultValue={formState.likelihoodId.value}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              likelihoodId: {
                                ...e.value,
                                value: parseInt(e.value.value, 10),
                              },
                            })
                          }}
                        />

                        <Field
                          disabled={props.lockEdit}
                          name={'description'}
                          component={FormTextArea}
                          label={'Quote description:'}
                          defaultValue={formState.description}
                          onChange={(e) => {
                            setFormState({ ...formState, description: e.value })
                          }}
                        />

                        {isBadDebter && (
                          <div style={{ 
                            backgroundColor: '#fff3cd', 
                            border: '1px solid #ffc107', 
                            borderRadius: '4px', 
                            padding: '12px', 
                            marginTop: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <FaFlag style={{ color: '#856404' }} />
                            <span style={{ color: '#856404', fontWeight: 'bold' }}>
                              Bad Debter - Contact accounts department
                            </span>
                          </div>
                        )}

                        <Divider>
                          Quote Info
                        </Divider>

                        <Field
                          disabled={props.lockEdit}
                          data={worktypeData}
                          name={'worktypeId'}
                          component={FormDropDownList}
                          label={'Choose the type of work:'}
                          defaultText={formState.worktypeId.text}
                          defaultValue={formState.worktypeId.value}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              worktypeId: {
                                ...e.value,
                                value: parseInt(e.value.value, 10),
                              },
                            })
                          }}
                          hasButton={props.lockEdit ? false : true}
                          buttonText={'Add Work Type'}
                          buttonAction={handleAddWorkType}
                        />

                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <p style={{ margin: "0 1em 0 0", top: "20px", position: "relative" }}><b>£</b></p>
                          <Field
                            disabled={props.lockEdit}
                            name={'cost'}
                            component={FormNumericTextBox}
                            label={'Value:'}
                            defaultText={formState.cost}
                            placeholder={formState.cost}
                            onChange={(e) => {
                              setFormState({ ...formState, cost: e.value })
                            }}
                          />
                        </div>

                        <Field
                          disabled={props.lockEdit}
                          name={'followupDate'}
                          component={FormDatePicker}
                          label={'Due date:'}
                          defaultText={formState.followupDate}
                          placeholder={dateFormatter.format(formState.followupDate)}
                          onChange={(e) => {
                            setFormState({ ...formState, followupDate: e.value })
                          }}
                        />

                        <Field
                          disabled={props.lockEdit}
                          data={employeeData}
                          originalData={employeeDataOrig}
                          name={'employeeAssignedToId'}
                          component={FormDropDownListFilter}
                          label={'Assigned to:'}
                          setData={setEmployeeData}
                          searchDropdown={searchEmployeeDropdown}
                          defaultItem={formState.employeeAssignedToId}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              employeeAssignedToId: {
                                ...e.value,
                                value: parseInt(e.value.value, 10),
                              },
                            })
                          }}
                        />
                        <Field
                          disabled={props.lockEdit}
                          name={'cost'}
                          component={FormSwitch}
                          label={'Sent'}
                          defaultText={formState.isSent}
                          placeholder={formState.isSent}
                          defaultValue={formState.isSent}
                          onChange={(e) => {
                            setFormState({ ...formState, isSent: e.value })
                          }}
                        />

                        {formState.isSent && formState.sentDate && (
                          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                            <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label">
                              First sent on: {new Date(formState.sentDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} at {new Date(formState.sentDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </label>
                          </div>
                        )}

                        <Field
                          disabled={props.lockEdit}
                          name={'requireFollowups'}
                          component={FormSwitch}
                          label={'Require Follow-ups'}
                          defaultText={formState.requireFollowups ?? true}
                          placeholder={formState.requireFollowups ?? true}
                          defaultValue={formState.requireFollowups ?? true}
                          onChange={(e) => {
                            setFormState({ ...formState, requireFollowups: e.value })
                          }}
                        />

                        {/* {
                            ! projectUrlAlreadySet && (
                              <Field
                                name={'folderStructure'}
                                component={FormSwitch}
                                label={'Create folder structure'}
                                defaultText={formState.projectFolderUrl === null ? false : true}
                                placeholder={formState.projectFolderUrl === null ? false : true}
                                defaultValue={formState.projectFolderUrl === null ? false : true}
                                onChange={(e) => {
                                  setFormState({ ...formState, projectFolderUrl: null })
                                  setFolderStructureToggleSet(e.value)
                                }}
                              />
                           )
                          
                          } */}

                        {
                          !projectUrlAlreadySet && props.lockEdit && (
                            <Field
                              data={[
                                { text: "M&E", value: 1 },
                                { text: "Construction", value: 2 }
                              ]}
                              name={'selectStructure'}
                              component={FormDropDownList}
                              label={'Select folder structure'}
                              onChange={(e) => {
                                setFormState({ ...formState, projectFolderUrl: e.value.text === undefined ? null : e.value.text })
                              }}
                            />
                          )
                        }

                        {
                          projectUrlAlreadySet && (
                            <>
                              <br />
                              <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label">Folder structure</label>
                              {checkForProjectUrl()}
                            </>

                          )
                        }

                        {
                          (!quoteUrlAlreadySet) && (
                            <Field
                              disabled={props.lockEdit}
                              data={quoteFiles.map(file => ({ text: file.fileName, value: file.quoteTemplateFileId }))}
                              name={'selectQuote'}
                              component={FormDropDownList}
                              label={'Select Quote template'}
                              onChange={(e) => {
                                setFormState({ ...formState, quoteUrl: e.value.text === undefined ? null : e.value.text })
                              }}
                            />
                          )
                        }
                        {
                          (quoteUrlAlreadySet) && (props.lockEdit === false) && (
                            <>
                              <br />
                              <br />

                              <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label">Quote URL</label>
                              {checkForQuoteUrl()?.content}
                              <br />
                              {console.log(checkForQuoteUrl())}
                              {
                                checkForQuoteUrl()?.hasUrl ? (
                                  <Button style={{marginTop:"10px"}} onClick={() => {setResetQuoteTemplate(true); setQuoteUrlAlreadySet(false)}}>Set new Quote Template</Button>
                                ) : null
                              }
                            </>

                          )
                        }                        

                        {displayConvert && <div className='overlay'></div>}

                        {displayConvert ? (
                          <div
                            className='form__convert__container'
                            style={animationStyle}
                          >
                            <div className='form__convert__header'>
                              <legend
                                className={'k-form-legend'}
                                style={{
                                  color: 'rgb(36,32,56)',
                                  paddingBottom: '1rem',
                                  fontWeight: 'bold',
                                  marginTop: '2rem',
                                }}
                              >
                                Convert to job:
                              </legend>
                            </div>

                            {convertText === 'New' && (
                              <>
                                <div style={{ marginBottom: '1rem' }}>
                                  <Label>Job description:</Label>
                                  <TextArea
                                    disabled={props.lockEdit}
                                    value={jobDescription}
                                    rows={6}
                                    valid={jobDescription.trim() !== ''}
                                    onChange={(e) => setJobDescription(String(e.value ?? ''))}
                                  />
                                  <div style={{ fontSize: '0.75rem', color: '#5a6472', marginTop: '0.25rem' }}>
                                    Starts as the quote's description and reference. Change it if the job needs something different.
                                  </div>
                                </div>

                                <Field
                                disabled={props.lockEdit}
                                  name={'startDate'}
                                  component={FormDateTimePicker}
                                  label={'Choose start date:'}
                                  validator={requiredValidator}
                                  onChange={(e) => {
                                    setFormState({ ...formState, startDate: e.value })
                                  }}
                                />

                                <Field
                                disabled={props.lockEdit}
                                  name={'endDate'}
                                  component={FormDateTimePicker}
                                  label={'Choose end date:'}
                                  validator={requiredValidator}
                                  onChange={(e) => {
                                    setFormState({ ...formState, endDate: e.value })
                                  }}
                                />

                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                  <p style={{ margin: "0 1em 0 0", top: "20px", position: "relative" }}><b>£</b></p>
                                  <Field
                                  disabled={props.lockEdit}
                                    name={'cost'}
                                    component={FormNumericTextBox}
                                    label={'Total Job Value:'}
                                    defaultText={formState.cost}
                                    placeholder={formState.cost}
                                    validator={requiredValidator}
                                    onChange={(e) => {
                                      setFormState({ ...formState, cost: e.value })
                                    }}
                                  />
                                </div>

                                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                  <Label>Notifiable Works:</Label>
                                  <MultiSelect
                                    disabled={props.lockEdit}
                                    data={notifiableWorksOptions}
                                    value={notifiableWorks}
                                    onChange={(e) => setNotifiableWorks(e.value)}
                                    placeholder="Select notifiable works..."
                                    required={true}
                                    valid={notifiableWorks.length > 0}
                                  />
                                  {notifiableWorks.length === 0 && (
                                    <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                      Please select at least one notifiable work.
                                    </div>
                                  )}
                                </div>

                                <div style={{ marginBottom: '1rem' }}>
                                  <Label>Copy to the job without prices:</Label>
                                  {quotePdfs.length === 0 ? (
                                    <div style={{ fontSize: '0.8rem', color: '#5a6472', marginTop: '0.25rem' }}>
                                      No PDFs are attached to this quote.
                                    </div>
                                  ) : (
                                    <>
                                      {quotePdfs.map((d) => (
                                        <label
                                          key={d.documentId}
                                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', fontSize: '0.85rem' }}
                                        >
                                          <input
                                            type='checkbox'
                                            disabled={props.lockEdit}
                                            checked={priceFreeIds.includes(d.documentId)}
                                            onChange={() => togglePriceFree(d.documentId)}
                                          />
                                          {d.fileName}
                                        </label>
                                      ))}
                                      <div style={{ fontSize: '0.75rem', color: '#5a6472', marginTop: '0.25rem' }}>
                                        Ticked PDFs are added to the job's files with every £ amount blacked out, and shown in the app.
                                      </div>
                                    </>
                                  )}
                                </div>
                                {
                                  props.lockEdit === false ? (
                                    <div className='button__container'>
                                      <button
                                        onClick={handleConvertSubmit}
                                        className='form__edit__button accept'
                                      >
                                        Save and Create Job
                                      </button>
                                      <button
                                        onClick={handleConvertDisplay}
                                        className='form__edit__button reject'
                                      >
                                        Cancel
                                      </button>
                                    </div>

                                  ) : null
                                }
                              </>
                            )}

                            {convertText === 'Existing' && (
                              <>
                                <Field
                                disabled={props.lockEdit}
                                  data={jobDropdownData}
                                  name={'jobId'}
                                  component={FormDropDownList}
                                  label={'Job no:'}
                                  placeholder={'Choose a job number:'}
                                  onChange={(e: any) => {
                                    setJobId(e.value.value)
                                  }}
                                />

                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                  <p style={{ margin: "0 1em 0 0", top: "20px", position: "relative" }}><b>£</b></p>
                                  <Field
                                  disabled={props.lockEdit}
                                    name={'cost'}
                                    component={FormNumericTextBox}
                                    label={'Total Job Value:'}
                                    defaultText={formState.cost}
                                    placeholder={formState.cost}
                                    validator={requiredValidator}
                                    onChange={(e) => {
                                      setFormState({ ...formState, cost: e.value })
                                    }}
                                  />
                                </div>

                                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                  <Label>Notifiable Works:</Label>
                                  <MultiSelect
                                    disabled={props.lockEdit}
                                    data={notifiableWorksOptions}
                                    value={notifiableWorks}
                                    onChange={(e) => setNotifiableWorks(e.value)}
                                    placeholder="Select notifiable works..."
                                  />
                                  <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                    Any selected works are added to the job's existing notifiable works.
                                  </div>
                                </div>
                                {
                                  props.lockEdit === false ? (
                                    <div className='button__container'>
                                      <button
                                        onClick={handleConvertSubmitExisting}
                                        className='form__edit__button accept'
                                      >
                                        Save and Update Job
                                      </button>
                                      <button
                                        onClick={handleConvertDisplay}
                                        className='form__edit__button reject'
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : null
                                }
                              </>
                            )}
                          </div>
                        ) : null}


                        {isDeleted && formState.lostReason && (
                          <div style={{
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c2c7',
                            borderRadius: '4px',
                            padding: '12px',
                            marginTop: '10px',
                            color: '#842029',
                            fontFamily: 'Heebo, sans-serif',
                            fontSize: '10pt',
                          }}>
                            <b>Lost reason:</b> {formState.lostReason}
                          </div>
                        )}

                        {showEmail && (
                          <QuoteEmailDialog
                            quoteId={quoteId}
                            refFormatted={formState.refFormatted}
                            onClose={() => setShowEmail(false)}
                            onSent={() => {
                              setShowEmail(false)
                              // Emailing marks the quote sent; keep a later save from undoing it.
                              setFormState((prev) => ({ ...prev, isSent: true }))
                              props.onRefresh?.()
                            }}
                          />
                        )}

                        {!props.lockEdit && (
                          <div className='button__container'>
                            {isDeleted ? (
                              <button
                                type="button"
                                className="form__edit__button accept"
                                onClick={handleQuoteWon}
                              >
                                 Re-instate Quote
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className='form__edit__button edit'
                                  disabled={
                                    formState.clientName.value === 0 ||
                                    formState.siteAddr.value === 0 ||
                                    formState.contact.contactId === 0
                                  }
                                  onClick={() => {
                                    handleSubmit();
                                    formRenderProps.onFormReset();
                                  }}
                                >
                                  Save changes
                                </button>
                                <button
                                  type="button"
                                  className='form__edit__button accept'
                                  disabled={!quoteId}
                                  onClick={() => setShowEmail(true)}
                                >
                                  <FaEnvelope style={{ marginRight: '0.4rem' }} />
                                  Email Quote
                                </button>
                                <SplitButton
                                  items={quoteConvertItems}
                                  onButtonClick={handleConvertDisplay}
                                  className='form__edit__button accept'
                                  text={`Convert to ${convertText}`}
                                  onItemClick={handleConvertChange}
                                />
                                <button
                                  type="button"
                                  className="form__edit__button reject"
                                  onClick={handleQuoteLost}
                                >
                                  Mark as Lost
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </fieldset>
                    </FormElement>
                  )}
                />

              </TabStripTab>
              {
                props.lockEdit === false ? (
                <TabStripTab title='File Upload'>
                  <TabSectionHeader
                    icon={<FaPaperclip />}
                    title="Files"
                    description="Upload and manage documents for this quote"
                  />
                  <div style={{ padding: '0 1rem 1rem' }}>
                  <ExternalDropZone
                    style={{ fontSize: '10pt', fontFamily: 'Heebo, sans-serif' }}
                    uploadRef={uploadRef}
                  />
                  <div style={{ height: '20px' }} />
                  <Upload
                    ref={uploadRef}
                    batch={true}
                    files={files}
                    multiple={true}
                    defaultFiles={[]}
                    withCredentials={false}
                    listItemUI={CustomListItemUI}
                    saveHeaders={{ Authorization: `Bearer ${token}` }}
                    saveUrl={token ? `https://${apiURL}/Files/Quote/${quoteId}` : ''}
                    onStatusChange={handleUploadSuccess}
                  />

                  <div style={{ height: '1.5rem' }} />

                  <div>
                    {documentData.length > 0 ? (
                      <div>
                        {documentData.map((document: fileDocumentTypes) => (
                          <QuoteDocumentRow
                            key={document.documentId}
                            onDocumentId={document.documentId}
                            onFileName={document.fileName}
                            onFileExtension={document.fileExtension}
                            appCanView={document.appCanView}
                            onQuoteId={quoteId}
                            isSurveyFile={document.isSurveyFile}
                            onDocumentDelete={handleDocumentDelete}
                          />
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '100%',
                          padding: '1rem',
                          backgroundColor: 'rgb(216 229 237)',
                          borderRadius: '0.5rem',
                          alignItems: 'center',
                        }}
                      >
                        <p style={{ margin: 0 }}>No files uploaded yet</p>
                      </div>
                    )}
                  </div>
                  </div>
                </TabStripTab>
                ) : null
              }
              <TabStripTab title='Workings'>
                <TabSectionHeader
                  icon={<FaHammer />}
                  title="Workings"
                  description="Material and labour breakdown for this quote"
                  actions={
                    <button
                      type="button"
                      onClick={() => workingSectionsRef.current?.openAddSection()}
                      className="jobsection__action-btn"
                    >
                      Add Section
                    </button>
                  }
                />
                <div style={{ padding: '0 1rem 1rem' }}>
                  <WorkingSectionsView
                    ref={workingSectionsRef}
                    hideToolbar
                    quoteId={quoteId}
                    lockEdit={props.lockEdit}
                    materialWorkings={materialWorkings}
                    onMaterialWorkingsChange={setMaterialWorkings}
                    labourWorkings={labourWorkings}
                    onLabourWorkingsChange={setLabourWorkings}
                  />

                  <WorkingsAdjustmentSummary
                    materialWorkings={materialWorkings}
                    labourWorkings={labourWorkings}
                    adjustmentPercent={workingsAdjustmentPercent}
                    onAdjustmentPercentChange={setWorkingsAdjustmentPercent}
                    lockEdit={props.lockEdit}
                  />
                </div>
              </TabStripTab>
              
              <TabStripTab title="Notes">
                <TabSectionHeader
                  icon={<FaRegStickyNote />}
                  title="Notes"
                  description="Internal notes and comments for this quote"
                />
                {formState.requireFollowups && !props.lockEdit && (
                  <div style={{ padding: '1em', paddingBottom: '0' }}>
                    <button
                      type="button"
                      onClick={handleFollowedUp}
                      style={{
                        width: '100%',
                        backgroundColor: '#ff8c00',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        fontFamily: 'Heebo, sans-serif',
                        height: '3rem',
                      }}
                    >
                      Mark as Followed Up
                    </button>
                  </div>
                )}
                <Notes sourceEntity={addNoteDTO} lockEdit={props.lockEdit}/>
              </TabStripTab>
              <TabStripTab title="Links">
                <TabSectionHeader
                  icon={<FaLink />}
                  title="Links"
                  description="Related web links and resources for this quote"
                />
                <EntityLinks sourceEntity={addNoteDTO} lockEdit={props.lockEdit} />
              </TabStripTab>
            </TabStrip>
            </>

          )
        }
      </div>

      {lostReasonDialogVisible && (
        <Dialog
          title='Why was this quote lost?'
          onClose={() => setLostReasonDialogVisible(false)}
          width={420}
        >
          <p style={{ margin: '0 0 1rem 0', fontFamily: 'Heebo, sans-serif', fontSize: '10pt' }}>
            Choose a reason before marking quote {formState.refFormatted} as lost.
          </p>
          <DropDownList
            data={lostReasons}
            textField='text'
            dataItemKey='value'
            value={selectedLostReason}
            loading={lostReasonsLoading}
            onChange={(e) => setSelectedLostReason(e.value)}
            style={{ width: '100%' }}
          />
          <DialogActionsBar>
            <button
              type='button'
              className='k-button k-button-md k-button-solid k-button-solid-base'
              onClick={() => setLostReasonDialogVisible(false)}
            >
              Cancel
            </button>
            <button
              type='button'
              className='k-button k-button-md k-button-solid k-button-solid-primary'
              onClick={confirmQuoteLost}
              disabled={!selectedLostReason}
            >
              Mark as Lost
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
}

export default EditQuoteForm
